// Everything that scrolls a past round into view goes through here: opening
// it, Show more, switching views and closing near the end. Two things differ
// by layout, and are handled only here:
// - What scrolls: the page on phones and tablets, the rail's body on desktop.
// - Zoom: tablets and desktop zoom the 390 design, so screen px (bounding
//   rects) differ from an element's own CSS px (heights, scrollHeight).
//   Older WebKit even reports rects without the zoom: measure with
//   screenRect, not getBoundingClientRect.
// Positions are screen px throughout; conversions happen at the scroller.

/** The zoom set on this element alone (1 when none). */
const ownZoom = (node: Element) =>
  parseFloat(window.getComputedStyle(node).zoom) || 1;

/** An element's total CSS zoom: screen px per CSS px of its own. */
export function screenZoom(el: Element): number {
  // The browser's own total, where supported (Chrome 128+, Safari 18+).
  const { currentCSSZoom } = el as Element & { currentCSSZoom?: number };
  if (typeof currentCSSZoom === 'number' && currentCSSZoom > 0)
    return currentCSSZoom;
  let zoom = ownZoom(el);
  for (let node = el.parentElement; node; node = node.parentElement)
    zoom *= ownZoom(node);
  return zoom;
}

// Whether getBoundingClientRect includes CSS zoom. Current engines report
// screen px. Older WebKit (an iPad Pro in Brave, 2026-10-28) divides a rect
// by the element's zoom, so inside the stage it's the stage's CSS px: copies
// flown on document.body landed shrunk toward the top-left, and reveals
// stopped short. Decided once, from a box whose layout is known.
let rectsOmitZoom: boolean | undefined;

function engineOmitsZoom(): boolean {
  if (rectsOmitZoom !== undefined) return rectsOmitZoom;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;top:0;left:0;width:100px;height:0;zoom:2;visibility:hidden;pointer-events:none';
  document.documentElement.appendChild(probe);
  const reported = probe.getBoundingClientRect().width;
  const own = probe.offsetWidth;
  probe.remove();
  // Nothing laid out (jsdom): no answer, so keep the engine's own rects.
  if (!own || !reported) return false;
  rectsOmitZoom = reported / own < 1.5;
  return rectsOmitZoom;
}

/** An element's or text Range's bounding rect in screen px, in every engine.
 *  Use it wherever a rect leaves the stage's zoom or meets the screen's. */
export function screenRect(target: Element | Range): DOMRect {
  const rect = target.getBoundingClientRect();
  if (!engineOmitsZoom()) return rect;
  const el =
    target instanceof Range
      ? target.startContainer instanceof Element
        ? target.startContainer
        : target.startContainer.parentElement
      : target;
  const zoom = el ? screenZoom(el) : 1;
  if (zoom === 1) return rect;
  // Older WebKit scales the document position, then subtracts the scroll.
  const { scrollX, scrollY } = window;
  return new DOMRect(
    (rect.x + scrollX) * zoom - scrollX,
    (rect.y + scrollY) * zoom - scrollY,
    rect.width * zoom,
    rect.height * zoom,
  );
}

/** The nearest ancestor that scrolls vertically, or null for the page. */
export function scrollerOf(el: Element): HTMLElement | null {
  for (
    let node = el.parentElement;
    node && node !== document.body && node !== document.documentElement;
    node = node.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

export interface ScrollFrame {
  /** The visible band of the scroller, captured when the frame is made. */
  top: number;
  bottom: number;
  /** The scroll position now. */
  position(): number;
  /** How far the content runs on below the visible band now. */
  remaining(): number;
  scrollTo(position: number, behavior?: ScrollBehavior): void;
  /** Where the browser reads overflow-anchor for this scroller. */
  anchorTargets: HTMLElement[];
}

/** What scrolls `el`, in screen px. */
export function scrollFrame(el: Element): ScrollFrame {
  const viewport = window.visualViewport;
  const screenTop = viewport?.offsetTop ?? 0;
  const screenBottom = screenTop + (viewport?.height ?? window.innerHeight);
  const scroller = scrollerOf(el);

  if (!scroller) {
    const content = document.getElementById('root') ?? document.body;
    return {
      top: screenTop,
      bottom: screenBottom,
      position: () => window.scrollY,
      remaining: () =>
        Math.max(
          0,
          content.getBoundingClientRect().bottom - window.innerHeight,
        ),
      scrollTo: (position, behavior = 'instant') =>
        window.scrollTo({ top: Math.max(0, position), behavior }),
      anchorTargets: [document.documentElement, document.body],
    };
  }

  // The scroller's own scroll units are its CSS px.
  const zoom = screenZoom(scroller);
  const box = screenRect(scroller);
  return {
    top: Math.max(screenTop, box.top),
    bottom: Math.min(
      screenBottom,
      box.top + (scroller.clientHeight || box.height / zoom) * zoom,
    ),
    position: () => scroller.scrollTop * zoom,
    remaining: () =>
      Math.max(
        0,
        (scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop) *
          zoom,
      ),
    scrollTo: (position, behavior = 'instant') =>
      scroller.scrollTo({ top: Math.max(0, position / zoom), behavior }),
    anchorTargets: [scroller],
  };
}
